import { supabase } from '@/lib/supabase';
import { recordSentMessage } from '@/lib/data/notifications';
import type {
  ChangeOrderRow, ChangeOrderRevisionRow, ChangeOrderStatus, ChangeOrderEventRow,
} from '@/types/database';

export const CHANGE_ORDER_STATUS_LABEL: Record<ChangeOrderStatus, string> = {
  // "Approved" is the display label; the stored value stays 'acknowledged'
  // (CLAUDE.md: never rename a stored enum value for wording).
  draft: 'Draft', sent: 'Sent', acknowledged: 'Approved',
  // "Cancelled" with two L's — Mick's preference, and it matches the
  // change_order_status enum value in the database. Don't "correct" it.
  rejected: 'Rejected', cancelled: 'Cancelled',
};

export interface NewChangeOrderInput {
  project_id: string;
  title: string;
  scope: string;
  cost_delta_cents?: number;
  /** 0038 — the change order's design fee. */
  designer_fee_cents?: number;
  affected_room_ids?: string[];
  affected_spec_ids?: string[];
  notes?: string | null;
}

/**
 * Every change order on a project, INCLUDING the ones in the bin (0046).
 *
 * The Deleted section is fed from the same array, split by
 * `partitionChangeOrders()`. A second query for it would add a round to every
 * project open, for a section almost nobody looks at — and the workspace loads
 * in one round on purpose (CLAUDE.md).
 */
export async function listChangeOrders(projectId: string): Promise<ChangeOrderRow[]> {
  const { data, error } = await supabase
    .from('change_orders')
    .select('*')
    .eq('project_id', projectId)
    .order('number', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function getChangeOrder(id: string): Promise<ChangeOrderRow | null> {
  const { data, error } = await supabase
    .from('change_orders')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export async function createChangeOrder(input: NewChangeOrderInput): Promise<ChangeOrderRow> {
  const { data, error } = await supabase
    .from('change_orders')
    .insert({
      project_id: input.project_id,
      title: input.title,
      scope: input.scope,
      cost_delta_cents: input.cost_delta_cents ?? 0,
      designer_fee_cents: input.designer_fee_cents ?? 0,
      affected_room_ids: input.affected_room_ids ?? [],
      affected_spec_ids: input.affected_spec_ids ?? [],
      notes: input.notes ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as ChangeOrderRow;
}

export async function updateChangeOrder(
  id: string,
  patch: Partial<Omit<ChangeOrderRow, 'id' | 'project_id' | 'created_at' | 'updated_at' | 'number'>>,
): Promise<ChangeOrderRow> {
  const { data, error } = await supabase
    .from('change_orders')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as ChangeOrderRow;
}

export async function deleteChangeOrder(id: string): Promise<void> {
  const { error } = await supabase
    .from('change_orders')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

/**
 * Mark a change order sent, and record the message that went out (2026-09-30).
 *
 * ─── WHAT THIS REPLACED ─────────────────────────────────────────────────────
 * Until now "Send" queued an SMS notification row for EVERY project contact
 * with a phone — clients and subcontractors alike — for the dispatcher that was
 * parked in favor of the native share sheet in Phase 19. Two things were wrong
 * with that: nothing has dispatched those rows since, so the client was never
 * actually told; and a subcontractor has no business seeing a change order's
 * design fee.
 *
 * It now works like a contractor reminder and a punch list: the VIEW hands the
 * message to `shareOrFallback()` inside the click (the gesture trap — see
 * share-sheet.ts), and calls this afterwards with the text that actually went.
 * The recipient is the client, and only the client.
 *
 * `text` is stored on the notification so the history shows and resends exactly
 * what was sent, rather than re-deriving it from numbers that may have changed
 * since (0044's rule).
 */
export async function sendChangeOrder(
  co: ChangeOrderRow,
  opts: { text: string; contactId?: string | null; phone?: string | null } = { text: '' },
): Promise<ChangeOrderRow> {
  const updated = await updateChangeOrder(co.id, { status: 'sent', sent_at: new Date().toISOString() });

  // Recording is a history entry, not the send itself — the message has
  // already left the device by the time this runs. A failure here must NOT
  // surface as a failed send: Phase 12 verification hit exactly that (the
  // notifications table didn't exist yet, the insert threw, and the UI said
  // "Send failed" for a change order that had been sent). Swallowed, as
  // punch-message.ts does for the same reason.
  if (opts.text) {
    try {
      await recordSentMessage({
        projectId: co.project_id,
        contactId: opts.contactId ?? null,
        kind: 'change_order_new',
        text: opts.text,
        phone: opts.phone ?? null,
      });
    } catch (err) {
      console.warn('[change-orders] send recorded locally only:',
        err instanceof Error ? err.message : err);
    }
  }
  return updated;
}

export async function acknowledgeChangeOrderViaShare(
  token: string,
  changeOrderId: string,
  typedName: string,
): Promise<boolean> {
  const { data, error } = await supabase.rpc('acknowledge_change_order', {
    token, co_id: changeOrderId, typed_name: typedName,
  });
  if (error) throw error;
  return data === true;
}

/**
 * Record an approval the designer received some other way — email, phone, a
 * meeting (0038). The database marks it as designer-recorded, stamps the
 * time, and from then on locks the change order like a portal approval.
 */
export async function recordChangeOrderApproval(id: string, approvedBy: string, note: string): Promise<ChangeOrderRow> {
  return updateChangeOrder(id, {
    status: 'acknowledged',
    acknowledged_by_name: approvedBy.trim(),
    approval_note: note.trim() || null,
  });
}

/**
 * Reopen an approved change order for editing (0041).
 *
 * The database does the rest of it: the approved version is filed into
 * change_order_revisions, the revision counter moves, and the approval is
 * cleared — so from this moment it stops counting toward the revised budget
 * and leaves the client's portal until it is sent again. The only thing sent
 * from here is the status; the history is not ours to write.
 */
export async function reviseChangeOrder(id: string): Promise<ChangeOrderRow> {
  return updateChangeOrder(id, { status: 'draft' });
}

/** Superseded versions of one change order, newest first. */
export async function listChangeOrderRevisions(changeOrderId: string): Promise<ChangeOrderRevisionRow[]> {
  const { data, error } = await supabase
    .from('change_order_revisions')
    .select('*')
    .eq('change_order_id', changeOrderId)
    .order('revision', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ChangeOrderRevisionRow[];
}

/** Declined by the client ('rejected') or withdrawn by the designer ('cancelled'). */
export async function closeChangeOrder(id: string, status: 'rejected' | 'cancelled'): Promise<ChangeOrderRow> {
  return updateChangeOrder(id, { status });
}

/**
 * Approved change-order money per project, for the projects list's revised
 * budgets — one query for every project the caller can see.
 */
export async function approvedChangeOrderMoney(): Promise<Array<{
  project_id: string; status: 'acknowledged'; cost_delta_cents: number; designer_fee_cents: number;
}>> {
  const { data, error } = await supabase
    .from('change_orders')
    .select('project_id, status, cost_delta_cents, designer_fee_cents')
    .eq('status', 'acknowledged');
  if (error) throw error;
  return (data ?? []) as Array<{ project_id: string; status: 'acknowledged'; cost_delta_cents: number; designer_fee_cents: number }>;
}

/**
 * Move a change order to Deleted (0046) — a column, not a delete.
 *
 * The database refuses this while it is approved: the client agreed to those
 * numbers and they move the revised budget. Revise or withdraw it first.
 */
export async function softDeleteChangeOrder(id: string): Promise<ChangeOrderRow> {
  return updateChangeOrder(id, { deleted_at: new Date().toISOString() });
}

/** Take one back out of Deleted, exactly as it was. */
export async function restoreChangeOrder(id: string): Promise<ChangeOrderRow> {
  return updateChangeOrder(id, { deleted_at: null });
}

/**
 * Put a withdrawn or declined change order back in play as a draft (0046).
 *
 * Deliberately NOT `reviseChangeOrder`, which sends the same status: a move out
 * of APPROVED files the approved version into the history and bumps the
 * revision counter, and a change order nobody approved has nothing to file.
 * The database tells the two apart and logs 'reopened' rather than 'revised';
 * these are two names for two different acts, on purpose.
 */
export async function reopenChangeOrder(id: string): Promise<ChangeOrderRow> {
  return updateChangeOrder(id, { status: 'draft' });
}

/**
 * Every event on a project's change orders, oldest first (0046).
 *
 * Scoped by project_id — the column is on the row, so this needs no join and
 * costs one request in the workspace's single round.
 */
export async function listChangeOrderEvents(projectId: string): Promise<ChangeOrderEventRow[]> {
  const { data, error } = await supabase
    .from('change_order_events')
    .select('*')
    .eq('project_id', projectId)
    .order('at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ChangeOrderEventRow[];
}

/**
 * A client's question from the share portal (0047).
 *
 * Does not change the status: a question is not a rejection, and a client who
 * has already approved and then asks something has not withdrawn their
 * agreement. Returns false for anything the token doesn't cover.
 */
export async function askChangeOrderQuestion(
  token: string, changeOrderId: string, typedName: string, body: string,
): Promise<boolean> {
  const { data, error } = await supabase.rpc('submit_change_order_question', {
    token, p_co_id: changeOrderId, typed_name: typedName, p_body: body,
  });
  if (error) throw error;
  return data === true;
}

/**
 * The designer's reply (0047).
 *
 * Through an RPC because `change_order_events` has no insert policy and must
 * keep it — a trail the app can write is a trail the app can rewrite. The name
 * on the reply is resolved in the database from the firm's own branding, not
 * sent from here.
 */
export async function answerChangeOrderQuestion(changeOrderId: string, body: string): Promise<string> {
  const { data, error } = await supabase.rpc('answer_change_order_question', {
    p_co_id: changeOrderId, p_body: body,
  });
  if (error) throw error;
  return data as string;
}
